// Gray reads as "other/none", so it's reserved for aggregates and kept out of
// the per-space palette.
const SPACE_COLOR_RAMPS = [
  "purple",
  "sky",
  "green",
  "orange",
  "pink",
  "teal",
  "yellow",
  "red",
];

export const SPACE_COLORS = SPACE_COLOR_RAMPS.map(
  (ramp) => `var(--${ramp}-600)`
);

// A space keeps the same ramp across every chart by indexing the palette on a
// stable id-sort, rather than each chart's own display order. Team spaces are
// indexed first so their colors never shift when the personal scope reveals
// personal spaces, which are appended after.
function spaceRampMap(
  spaces: ReadonlyArray<{ id: string; mode: string }>
): Map<string, string> {
  const map = new Map<string, string>();
  const byMode = (personal: boolean) =>
    spaces
      .filter((space) => (space.mode === "personal") === personal)
      .map((space) => space.id)
      .sort();
  [...byMode(false), ...byMode(true)].forEach((id, i) =>
    map.set(id, SPACE_COLOR_RAMPS[i % SPACE_COLOR_RAMPS.length]!)
  );
  return map;
}

export function spaceColorMap(
  spaces: ReadonlyArray<{ id: string; mode: string }>
): Map<string, string> {
  const map = new Map<string, string>();
  for (const [id, ramp] of spaceRampMap(spaces)) {
    map.set(id, `var(--${ramp}-600)`);
  }
  return map;
}

// The 200 tint of each space's ramp, used as the solid "unused" headroom track
// in budget donuts so remaining allowance reads as a crisp light shade rather
// than a semi-transparent wash of the 600.
export function spaceTrackColorMap(
  spaces: ReadonlyArray<{ id: string; mode: string }>
): Map<string, string> {
  const map = new Map<string, string>();
  for (const [id, ramp] of spaceRampMap(spaces)) {
    map.set(id, `var(--${ramp}-200)`);
  }
  return map;
}

const MONITORING_CHART_HEIGHT = 160;
export const MONITORING_PANEL_PADDING = 32;
export const MONITORING_PANEL_GAP = 20;

// Minimum card edge in the collapsed 1-D layouts, so charts stay readable.
export const MONITORING_CARD_MIN = 340;
// Per-row height floor for the monitoring grids — shorter than the card width
// floor above because the charts read fine at this row height, so the grids keep
// their multi-row shape on small laptops instead of collapsing to the
// horizontal-scroll row too soon.
export const MONITORING_ROW_MIN = 255;
// Cap for content-hugging cards (executions by space) in the single-row layout,
// so a chart with many spaces grows but never dominates before its bars compress.
export const MONITORING_CARD_MAX = 720;

// Viewport height consumed around the grid (app header, monitoring tabs, and
// layout paddings): measures 125px, kept with slack so the grid collapses
// slightly before it would have to scroll.
const MONITORING_GRID_CHROME = 137;
const MONITORING_GRID_ROW_GAP = 12;

// Below this viewport height the Workflows grid collapses to a single
// horizontally-scrolling row — the height-axis mirror of the single-column
// collapse at narrow widths — so the charts never shrink to a tiny "small state".
export const MONITORING_SHORT_VIEWPORT_HEIGHT =
  MONITORING_GRID_CHROME + 2 * MONITORING_ROW_MIN + MONITORING_GRID_ROW_GAP;

const ACTIVE_HEADER_HEIGHT = 64;
const CHART_WITH_LABELS = MONITORING_CHART_HEIGHT + 8 + 12;

export const MONITORING_PANEL_HEIGHT =
  MONITORING_PANEL_PADDING * 2 +
  ACTIVE_HEADER_HEIGHT +
  MONITORING_PANEL_GAP +
  CHART_WITH_LABELS;
